import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext(null)

export const ROLES = {
  USER:           'user',
  STAFF:          'staff',
  READONLY_ADMIN: 'readonly_admin',
  ADMIN:          'admin',
  SUPERADMIN:     'superadmin',
}

const ADMIN_ROLES = [ROLES.STAFF, ROLES.READONLY_ADMIN, ROLES.ADMIN, ROLES.SUPERADMIN]
const EDIT_ROLES  = [ROLES.STAFF, ROLES.ADMIN, ROLES.SUPERADMIN]

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  // Fix #10: destructure error and only update profile on success
  async function fetchProfile(userId) {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, role, full_name')
      .eq('id', userId)
      .single()
    if (!error) setProfile(data)
  }

  useEffect(() => {
    // Fix #12: await fetchProfile before setting loading=false so that
    // loading=true for the entire auth+profile initialisation sequence.
    async function init() {
      const { data: { session } } = await supabase.auth.getSession()
      // Don't populate app auth state for a recovery session — the user
      // hasn't finished resetting their password yet.
      if (window.location.pathname !== '/reset-password') {
        setUser(session?.user ?? null)
        if (session?.user) await fetchProfile(session.user.id)
      }
      setLoading(false)
    }
    init()

    // Listen for future auth state changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (_event === 'PASSWORD_RECOVERY') {
          // Redirect to the reset-password page so the user can set a new
          // password. Using window.location so this works regardless of
          // where in the app the recovery link lands the user.
          window.location.replace('/reset-password')
          return
        }
        // Don't update auth state while the user is on the reset-password page.
        if (window.location.pathname === '/reset-password') {
          return
        }
        setUser(session?.user ?? null)
        if (session?.user) {
          fetchProfile(session.user.id)
        } else {
          setProfile(null)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  /** True if the current user's role is in the given array */
  function hasRole(requiredRoles) {
    if (!profile) return false
    return requiredRoles.includes(profile.role)
  }

  /** True for roles that can create/edit/delete (not readonly_admin) */
  function canEdit() {
    return hasRole(EDIT_ROLES)
  }

  /** True for any admin-level role */
  function isAdminRole() {
    return hasRole(ADMIN_ROLES)
  }

  /** True only for staff */
  function isStaff() {
    return profile?.role === ROLES.STAFF
  }

  /** True only for superadmin */
  function isSuperAdmin() {
    return profile?.role === ROLES.SUPERADMIN
  }

  const signIn = (email, password) =>
    supabase.auth.signInWithPassword({ email, password })

  const signUp = (email, password) =>
    supabase.auth.signUp({ email, password })

  const signOut = () => supabase.auth.signOut()

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      loading,
      signIn,
      signUp,
      signOut,
      hasRole,
      canEdit,
      isAdminRole,
      isStaff,
      isSuperAdmin,
      ROLES,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
